import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import Header from '../components/Header';
import ContentReader from '../components/ContentReader';
import VideoModal from '../components/VideoModal';
import HistoryButton from '../components/HistoryButton';
import StepHelpButton from '../components/StepHelpButton';
import HistoryModal from '../components/HistoryModal';
import { BreadcrumbItem } from '../components/Header';
import { useDialog } from '../context/DialogContext';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import { progressService } from '../services/progressService';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import { getStepActionSchema } from '../config/stepActionSchemas';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import StepShareDialog from '../components/StepShareDialog';
import { useStepShareDialog } from '../hooks/useStepShareDialog';
import { withShareButton } from '../utils/stepShareUtils';
import { 
  STEP2_QUESTIONS, 
  migrateQuestionData, 
  unmappedQuestionData, 
  type QuestionData 
} from '../utils/questionMapping';

interface Step2Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

// Using imported STEP2_QUESTIONS from questionMapping.ts

const Step2: React.FC<Step2Props> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const { showHelp } = useDialog();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Understand what Step Two in SAA is about and why it's important",
      description: "Discover how belief in a power greater than myself restored sanity for others-and how it can for me",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "How to work Step Two (guidance & video)",
      description: "Read guidance and watch video on how to work through Step Two",
      completed: false,
      buttons: [
        {
          text: "Read Guidance",
          action: () => setIsGuidanceOpen(true),
          variant: 'primary'
        },
        {
          text: "Watch Video",
          action: () => setIsVideoModalOpen(true),
          variant: 'secondary'
        }
      ]
    },
    {
      id: 3,
      title: "Answer Step Two questions",
      description: "Reflect honestly on spirituality, higher power, and related experiences — there's no right answer, just your story.",
      completed: false
    },
    {
      id: 4,
      title: "Share and review your work with your sponsor",
      description: "Review your Step Two work with your sponsor. You can check this off when you've shared or discussed your work — using the share button is optional.",
      completed: false
    },
    {
      id: 5,
      title: "Take Step Two: Declare willingness",
      description: "I am willing to believe that a power greater than myself can restore me to sanity",
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  const [questionAnswers, setQuestionAnswers] = useState<QuestionData>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const { openShareDialog, shareDialogProps } = useStepShareDialog(2, user, actions, () => ({
    questionAnswers,
  }));
  
  const [showQuestions, setShowQuestions] = useState(false);
  const [savingQuestions, setSavingQuestions] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Two', path: '/saa/12-steps/step-2', active: true }
  ];

  const refreshStepData = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);

    try {
      perfMark('Step2', 'refreshStepData start');
      const { steps, journeyProgress } = await perfTimeAsync('Step2 refreshStepData (total)', () =>
        progressService.loadStepPagesProgress('SAA', '12-steps', [2], { includeJourney: true })
      );

      const existingProgress = steps[2] ?? null;

      if (journeyProgress) {
        setIsOptedOut(journeyProgress.opted_out);
      }

      if (existingProgress) {
        const savedData = existingProgress.data || {};
        setStepData(existingProgress);

        if (savedData.actions) {
          const merged = mergeStepActions(2, originalActions, savedData, !!existingProgress.completed);
          setActions(merged.actions);
          if (merged.migrationApplied) {
            await progressService.updateStepProgress({
              program: 'SAA',
              track: '12-steps',
              step_number: 2,
              step_title: 'Step Two',
              completed: !!existingProgress.completed,
              data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, savedData),
            });
          }
        }

        let loadedQuestionAnswers: QuestionData = {};

        if (savedData.step2_questions) {
          const firstKey = Object.keys(savedData.step2_questions)[0];

          if (firstKey && firstKey.startsWith('q')) {
            loadedQuestionAnswers = savedData.step2_questions;
          } else {
            loadedQuestionAnswers = migrateQuestionData(2, savedData.step2_questions);
          }

          setQuestionAnswers(loadedQuestionAnswers);
        } else if (savedData.questionAnswers) {
          loadedQuestionAnswers = migrateQuestionData(2, savedData.questionAnswers);
          setQuestionAnswers(loadedQuestionAnswers);
        } else if (savedData.answers) {
          const firstKey = Object.keys(savedData.answers)[0];
          if (firstKey && firstKey.startsWith('q')) {
            loadedQuestionAnswers = savedData.answers;
            setQuestionAnswers(loadedQuestionAnswers);
          } else {
            loadedQuestionAnswers = migrateQuestionData(2, savedData.answers);
            setQuestionAnswers(loadedQuestionAnswers);
          }
        }

        if (Object.keys(loadedQuestionAnswers).length > 0) {
          const simpleAnswers = unmappedQuestionData(loadedQuestionAnswers);
          setDraftAnswers(simpleAnswers);
        }
      }

      clearError();
      perfSummary('Step Two page');
    } catch (err: any) {
      console.error('Error loading step progress:', err);
      handleError(err, 'Failed to load your progress. Please try refreshing the page.');
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    refreshStepData();
  }, [user, stepWorkspaceRevision]);

  // Add useEffect to load saved answers into draftAnswers
  useEffect(() => {
    // Always initialize draftAnswers with saved questionAnswers when they change
    if (Object.keys(questionAnswers).length > 0) {
      // Convert QuestionData to simple key-value format for draft
      const simpleAnswers = unmappedQuestionData(questionAnswers);
      setDraftAnswers(simpleAnswers);
    }
  }, [questionAnswers]);

  const handleGreenBookClick = () => {
    setIsContentReaderOpen(true);
  };

  // Help functions
  const openHelp = async (questionId: string) => {
    try {
      const response = await fetch('/content/Step_2_Guidance.md');
      const text = await response.text();
      
      const startTag = `<<${questionId}-help start>>`;
      const endTag = `<<${questionId}-help end>>`;
      
      const startIndex = text.indexOf(startTag);
      const endIndex = text.indexOf(endTag);
      
      if (startIndex !== -1 && endIndex !== -1) {
        const helpContent = text.substring(startIndex + startTag.length, endIndex).trim();
        showHelp({
          title: 'Help',
          content: helpContent
        });
      } else {
        showHelp({
          title: 'Help',
          content: 'Help content not found for this question.'
        });
      }
    } catch (error) {
      console.error('Error loading help content:', error);
      showHelp({
        title: 'Help',
        content: 'Error loading help content.'
      });
    }
  };

  const [draftAnswers, setDraftAnswers] = useState<Record<string, string>>({});
  const [editingAnswers, setEditingAnswers] = useState(false);
  
  // Auto-save state management
  const [savingAnswers, setSavingAnswers] = useState(false);
  const [savedAnswers, setSavedAnswers] = useState(false);
  const [saveAnswersError, setSaveAnswersError] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize draftAnswers from questionAnswers when they change (unless user is actively editing)
  useEffect(() => {
    if (!editingAnswers) {
      // Convert QuestionData to simple key-value format for draft
      const simpleAnswers = unmappedQuestionData(questionAnswers);
      setDraftAnswers(simpleAnswers);
    }
  }, [questionAnswers, editingAnswers]);

  // Auto-save answers with debouncing
  const autoSaveAnswers = async () => {
    // Clear any existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set debounce timeout (300ms)
    saveTimeoutRef.current = setTimeout(async () => {
      // Check if data actually changed
      const currentSimpleAnswers = unmappedQuestionData(questionAnswers);
      const hasChanges = JSON.stringify(draftAnswers) !== JSON.stringify(currentSimpleAnswers);
      
      if (!hasChanges) return;

      // Set saving state
      setSavingAnswers(true);
      setSaveAnswersError(false);
      setSavedAnswers(false);

      try {
        // Convert draft answers to mapped format for saving
        const mappedAnswers: QuestionData = {};
        Object.entries(draftAnswers).forEach(([key, answer]) => {
          const question = STEP2_QUESTIONS.find(q => q.key === key);
          if (question) {
            mappedAnswers[question.id] = { key, answer: answer as string };
          }
        });

        // Save to backend
        const currentActions = [...actions];
        await progressService.updateStepProgress({
          program: 'SAA',
          track: '12-steps',
          step_number: 2,
          step_title: 'Step Two',
          completed: currentActions.every(action => action.completed),
          data: stepDataWithActions(currentActions, getStepActionSchema(2).actionSchemaVersion, {
            step2_questions: mappedAnswers
          })
        });

        // Update local state
        setQuestionAnswers(mappedAnswers);
        setEditingAnswers(false);

        // Show success indicator
        setSavingAnswers(false);
        setSavedAnswers(true);
        setTimeout(() => setSavedAnswers(false), 2000);

      } catch (err: any) {
        console.error('Error auto-saving answers:', err);
        setSavingAnswers(false);
        setSaveAnswersError(true);
        setTimeout(() => setSaveAnswersError(false), 3000);
        handleError(err, 'Failed to save answers. Please try again.');
      }
    }, 300);
  };

  const allCompleted = actions.every(action => action.completed);

  // Update the StepActions onActionChange to include validation
  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      handleError(new Error('User not logged in'), 'Please log in to save your progress.');
      return;
    }
    
    // Check if action 3 is being marked complete
    const action3 = updatedActions.find(a => a.id === 3);
    const currentAction3 = actions.find(a => a.id === 3);
    
    let answersToSave = questionAnswers; // Default to existing saved answers
    
    if (action3 && !currentAction3?.completed && action3.completed) {
      // Auto-save draft answers before marking complete
      // Convert draft answers to mapped format for saving
      const mappedAnswers: QuestionData = {};
      Object.entries(draftAnswers).forEach(([key, answer]) => {
        const question = STEP2_QUESTIONS.find(q => q.key === key);
        if (question) {
          mappedAnswers[question.id] = { key, answer: answer as string };
        }
      });
      
      // Update the answers to save
      answersToSave = mappedAnswers;
      // Update state so it's in sync
      setQuestionAnswers(mappedAnswers);
      setEditingAnswers(false);
    }
    
    // Normal action change handling for all actions
    try {
      // Update local state first
      setActions(updatedActions);
      
      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 2,
        step_title: 'Step Two',
        completed: allCompleted,
        data: stepDataWithActions(updatedActions, getStepActionSchema(2).actionSchemaVersion, {
          step2_questions: answersToSave
        })
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }
      
      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Two - ${completedCount} of 5 actions completed`,
        progress_milestone: allCompleted ? 'step2_completed' : 'step2_in_progress'
      });
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, 'Failed to save progress. Please try again.');
    }
  };



  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogin={onLogin}
        onLogout={onLogout}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Step Two</h1>
            <StepHelpButton stepKey="app" />
            {stepData && (
              <HistoryButton
                stepNumber={2}
                stepTitle="Step Two"
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>
            Came to believe that a Power greater than ourselves could restore us to sanity.
          </p>
        </div>

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
              </>
            )}
          </div>
        )}

        {!user && !isInitializing && !errorMessage && <StepLoginPrompt onLogin={onLogin} />}

        {user && isInitializing && !errorMessage && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {user && !isInitializing && !errorMessage && (
          <>
            <StepActions
          actions={withShareButton(2, actions, openShareDialog)}
          onActionChange={handleActionChange}
          renderActionContent={(action, index) => {
            if (action.id === 3) {
              return (
                <div className={styles.actionContentIndent}>
                  <div style={{ marginBottom: 16, width: '100%' }}>
                    {/* Auto-save indicator */}
                    <div style={{ 
                      textAlign: 'right', 
                      marginBottom: 16, 
                      minHeight: 24,
                      fontSize: '14px',
                      fontWeight: 500
                    }}>
                      {savingAnswers && (
                        <span style={{ color: '#6c757d' }}>
                          Saving...
                        </span>
                      )}
                      {savedAnswers && (
                        <span style={{ color: '#28a745' }}>
                          ✓ All changes saved
                        </span>
                      )}
                      {saveAnswersError && (
                        <span style={{ color: '#dc3545' }}>
                          ⚠ Save failed. Please try again.
                        </span>
                      )}
                    </div>
                    {STEP2_QUESTIONS.map((q, idx) => (
                      <div key={q.key} style={{ marginBottom: 16, width: '100%' }}>
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: 8, 
                          width: '100%'
                        }}>
                          <span style={{ 
                            fontWeight: 600, 
                            color: '#333', 
                            fontSize: '1.1rem',
                            marginRight: '8px',
                            flexShrink: 0,
                            minWidth: '20px'
                          }}>
                            {idx + 1}.
                          </span>
                          <span style={{ 
                            fontWeight: 500, 
                            color: '#333', 
                            fontSize: '0.95rem',
                            lineHeight: '1.4',
                            flex: 1
                          }}>
                            {q.label}
                          </span>
                          <button 
                            type="button" 
                            className={styles.helpBtn} 
                            onClick={() => openHelp(`step2-question-${idx + 1}`)}
                            title="Get help with this question"
                            style={{ marginLeft: '8px' }}
                            tabIndex={-1}
                          >
                            ?
                          </button>
                        </div>
                        <textarea
                          value={draftAnswers[q.key] || ''}
                          onChange={e => {
                            setDraftAnswers(ans => ({ ...ans, [q.key]: e.target.value }));
                            setEditingAnswers(true);
                          }}
                          onBlur={autoSaveAnswers}
                          placeholder={
                            q.key === 'spirituality_childhood' ? 'e.g., In my family, religion was strict and scary. It made me feel disconnected...' :
                            q.key === 'spirituality_now' ? 'e.g., Right now, I think of spirituality as something I\'m exploring. I\'m open to...' :
                            q.key === 'higher_power' ? 'e.g., My higher power is the group and the connection I feel there. It\'s not religious but...' :
                            q.key === 'anger_higher_power' ? 'e.g., I felt angry when my prayers weren\'t answered. I thought a higher power should have...' :
                            q.key === 'prayer_deals' ? 'e.g., One time I prayed when I was desperate to stop acting out. I told God I would...' :
                            q.key === 'authority_abuse' ? 'e.g., Someone in my life had control over me when I was young. It made it hard for me to trust...' :
                            'Start typing your answer here...'
                          }
                          rows={4}
                          style={{ 
                            width: '100%', 
                            borderRadius: 4, 
                            border: '1px solid #ccc', 
                            padding: '8px 12px',
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          }}
        />

          {allCompleted && (
            <>
              <div className={styles.completionMessage}>
                <h3>🎉 Congratulations!</h3>
                <p>You have completed all actions for Step Two and can move on to Step Three.</p>
              </div>
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <a href="/saa/12-steps/step-1" className={styles.prevStepLink}>
                    {'<<'} Step One
                  </a>
                  <span></span>
                  <a href="/saa/12-steps/step-3" className={styles.nextStepLink}>
                    Step Three {'>>'}
                  </a>
                </div>
              </div>
            </>
          )}

          {!allCompleted && (
            <div className={styles.stepNavigation}>
              <div className={styles.navigationLinks}>
                <a href="/saa/12-steps/step-1" className={styles.prevStepLink}>
                  {'<<'} Step One
                </a>
                <span></span>
                <a href="/saa/12-steps/step-3" className={styles.nextStepLink}>
                  Step Three {'>>'}
                </a>
              </div>
            </div>
          )}
          </>
        )}
        </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-two start>>"
        endTag="<<step-two end>>"
      />

      {/* ContentReader for How to Work Step Two */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_2_Guidance.md"
        startTag="<<step2-guidance start>>"
        endTag="<<step2-guidance end>>"
        title="Guidance on working Step Two"
      />
      
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoId="Zfs_Q_ah0ZQ"
        title="Step Two Video Guide"
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={2}
        stepTitle="Step Two"
        stepData={stepData}
        onStepUpdated={() => {
          setIsHistoryModalOpen(false);
          if (user) {
            void refreshStepData();
          }
        }}
      />
      <StepShareDialog {...shareDialogProps} />
    </div>
  );
};

export default Step2; 
